import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import Tabs, { Tab, TabPanel } from '@ingka/tabs';
import Text from '@ingka/text';
import { useLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import type { GetRetailUnitResponse } from '@lotta/shared/types-lottery-api';
import { swipeImageDomainWhitelistPattern } from '@lotta/shared/types-lottery-schemas';
import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../../LotteryForm.module.scss';
import { ControlledTextArea } from '../../../components/controlled-text-area/ControlledTextArea';
import { FindImageInfoTheatre } from '../../../components/find-image-info-theatre/FindImageInfoTheatre';
import { mapRecord } from '../../../utils';
import type { QuestionsErrorFormatLimitsType } from '../../../utils/error';
import { useFormModule } from '../../../utils/use-form-module';
import type { FormModuleProps, ModuleTransformer } from '../../types';
import { ControlledInputSwipeQuestions } from '../ControlledInputSwipeQuestions';
export const SwipeTranslationsLinksSchema = Type.Object({
  startPageImage: Type.String({
    format: 'uri',
    maxLength: 1000,
    pattern: swipeImageDomainWhitelistPattern,
    errorMessage: {
      format: 'form-error-message-uri',
      pattern: 'form-error-message-image-pattern',
      maxLength: 'form-lottery-name-error-message-max-length',
    },
  }),
  winnerPageImage: Type.String({
    format: 'uri',
    maxLength: 1000,
    pattern: swipeImageDomainWhitelistPattern,
    errorMessage: {
      format: 'form-error-message-uri',
      pattern: 'form-error-message-image-pattern',
      maxLength: 'form-lottery-name-error-message-max-length',
    },
  }),
  loserPageImage: Type.String({
    format: 'uri',
    maxLength: 1000,
    pattern: swipeImageDomainWhitelistPattern,
    errorMessage: {
      format: 'form-error-message-uri',
      pattern: 'form-error-message-image-pattern',
      maxLength: 'form-lottery-name-error-message-max-length',
    },
  }),
});

export const SwipeTranslationsQuestionSchema = Type.Object({
  question: Type.String({
    minLength: 4,
    maxLength: 120,
    errorMessage: {
      minLength: 'swipe-upload-questions-question-error-message-min-length',
      maxLength: 'swipe-upload-questions-question-error-message-max-length',
    },
  }),
  description: Type.String({
    minLength: 2,
    maxLength: 150,
    errorMessage: {
      minLength: 'swipe-upload-questions-description-error-message-min-length',
      maxLength: 'swipe-upload-questions-description-error-message-max-length',
    },
  }),
  correctAnswerText: Type.Optional(
    Type.String({
      minLength: 2,
      maxLength: 30,
      errorMessage: {
        minLength: 'swipe-upload-questions-correct-answer-text-error-message-min-length',
        maxLength: 'swipe-upload-questions-correct-answer-text-error-message-max-length',
      },
    })
  ),
  wrongAnswerText: Type.Optional(
    Type.String({
      minLength: 2,
      maxLength: 30,
      errorMessage: {
        minLength: 'swipe-upload-questions-wrong-answer-text-error-message-min-length',
        maxLength: 'swipe-upload-questions-wrong-answer-text-error-message-max-length',
      },
    })
  ),
  answer: Type.Boolean(),
  image: Type.String({
    format: 'uri',
    maxLength: 500,
    pattern: swipeImageDomainWhitelistPattern,
    errorMessage: {
      format: 'form-error-message-uri',
      pattern: 'form-error-message-image-pattern',
      maxLength: 'swipe-upload-questions-image-error-message-max-length',
    },
  }),
});

export const SwipeTranslationsItemsSchema = {
  maxItems: 100,
  errorMessage: {
    // eslint-disable-next-line i18next/no-literal-string
    maxItems: 'swipe-upload-questions-error-message-items-max-number',
  },
};

export const SwipeTranslationsQuestionsSchema = Type.Array(
  SwipeTranslationsQuestionSchema,
  SwipeTranslationsItemsSchema
);

const SwipeTranslationSchema = Type.Object({
  title: Type.String({
    minLength: 1,
    maxLength: 100,
    errorMessage: {
      minLength: 'form-lottery-name-error-message-min-length',
      maxLength: 'form-lottery-name-error-message-max-length',
    },
  }),
  body: Type.String({
    minLength: 1,
    maxLength: 1500,
    errorMessage: {
      minLength: 'form-lottery-name-error-message-min-length',
      maxLength: 'form-lottery-name-error-message-max-length',
    },
  }),
  questions: SwipeTranslationsQuestionsSchema,
  links: SwipeTranslationsLinksSchema,
});

export type SwipeTranslationsPartialFormType = Static<typeof SwipeTranslationsPartialFormSchema>;
export const SwipeTranslationsPartialFormSchema = Type.Object(
  {
    translation: Type.Record(
      Type.String(), // language code
      SwipeTranslationSchema
    ),
  },
  { additionalProperties: true, $id: 'SwipeTranslationsSchema' }
);

export const SwipeTranslationsTransformer: ModuleTransformer<SwipeTranslationsPartialFormType> = {
  fromPayload: ({ translation }) => {
    return {
      translation: mapRecord(translation, (translation) => ({
        title: translation.swipe?.title ?? '',
        body: translation.swipe?.body ?? '',
        questions: translation.swipe?.questions ?? [],
        links: {
          startPageImage: '',
          winnerPageImage: '',
          loserPageImage: '',
          ...translation.swipe?.links,
        },
      })),
    };
  },
  toPayload: (formData) => {
    return {
      translation: mapRecord(formData.translation, (translation) => ({
        swipe: {
          title: translation.title,
          body: translation.body,
          questions: translation.questions,
          links: translation.links,
        },
      })),
    };
  },
};

export type SwipeTranslationsPartialFormProps =
  FormModuleProps<SwipeTranslationsPartialFormType> & {
    retailUnitData: GetRetailUnitResponse['retailUnit'];
    moduleName: string;
    requiredNumberOfQuestions: number;
  };

export const questionsErrorFormatLimit: QuestionsErrorFormatLimitsType = {
  questionMinLength: SwipeTranslationsQuestionSchema.properties.question.minLength,
  questionMaxLength: SwipeTranslationsQuestionSchema.properties.question.maxLength,
  descriptionMinLength: SwipeTranslationsQuestionSchema.properties.description.minLength,
  descriptionMaxLength: SwipeTranslationsQuestionSchema.properties.description.maxLength,
  correctAnswerTextMinLength:
    SwipeTranslationsQuestionSchema.properties.correctAnswerText.minLength,
  correctAnswerTextMaxLength:
    SwipeTranslationsQuestionSchema.properties.correctAnswerText.maxLength,
  wrongAnswerTextMinLength: SwipeTranslationsQuestionSchema.properties.wrongAnswerText.minLength,
  wrongAnswerTextMaxLength: SwipeTranslationsQuestionSchema.properties.wrongAnswerText.maxLength,
  imageMaxLength: SwipeTranslationsQuestionSchema.properties.image.maxLength,
  maxNumberItems: SwipeTranslationsItemsSchema.maxItems,
};

/**
 * Swipe translations (the language tabs part)
 *
 * This is split into its own form so that we can show an error message if any translations are
 * missing (validation fails) independent of other parts of the swipe module.
 */
export const SwipeTranslationsPartialForm: React.FC<SwipeTranslationsPartialFormProps> = ({
  retailUnitData,
  moduleName,
  values,
  onChange,
  onValidation,
  requiredNumberOfQuestions,
}) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { isValid },
    setError,
    clearErrors,
    trigger,
  } = useFormModule({
    moduleName,
    defaultValues: values,
    schema: SwipeTranslationsPartialFormSchema,
    onChange,
    onValidation,
  });

  // clearErrors() does not affect isValid, but since we want that here it is
  // grouped with trigger().
  const clearErrorsAndTriggerValidation: typeof clearErrors = useCallback(
    (name) => {
      clearErrors(name);
      // clearErrors() accepts error-only paths (e.g. 'root', 'root.*') that are not
      // valid field paths for trigger(). Those never reach this callback, so narrow
      // to the field-path type trigger() expects.
      trigger(name as Parameters<typeof trigger>[0]);
    },
    [clearErrors, trigger]
  );

  const { language } = useLocale();

  const languageNames = new Intl.DisplayNames([language], { type: 'language' });
  const [currentTheatreTab, setCurrentTheatreTab] = useState('');

  const tabsAndPanels = retailUnitData.languages.map((language) => {
    const id = `${moduleName}-tab--${language}`;
    return {
      id,
      tab: <Tab key={language} text={languageNames.of(language)} tabPanelId={id} type="button" />,
      panel: (
        <TabPanel key={language} tabPanelId={id}>
          <div className={styles['form-elements']}>
            <Text bodySize="m" className={styles['config-section']}>
              <b>{t('form-sign-up-swipe-config-upload-questions-explanation')}</b>
            </Text>
            {!Number.isNaN(requiredNumberOfQuestions) && (
              <Text bodySize="m" className={styles['config-section']}>
                <b>
                  {t('form-sign-up-swipe-config-upload-questions-required-number-of-questions', {
                    requiredNumberOfQuestions: requiredNumberOfQuestions,
                  })}
                </b>
              </Text>
            )}
            <ControlledInputSwipeQuestions
              name={`translation.${language}.questions`}
              control={control}
              language={language}
              setError={setError}
              clearErrors={clearErrorsAndTriggerValidation}
              requiredNumberOfQuestions={requiredNumberOfQuestions}
              limits={questionsErrorFormatLimit}
            />
            <Text bodySize="m" className={styles['config-section']}>
              <b>{t('form-sign-up-swipe-config-ingka-dam-search-label')}</b>
            </Text>
            <div className={styles['form-elements']}>
              <FindImageInfoTheatre
                open={currentTheatreTab === id}
                onClose={() => setCurrentTheatreTab('')}
              />
              <Button
                text={t('find-image-theatre-button-text')}
                type="secondary"
                onClick={() => setCurrentTheatreTab(id)}
              />
            </div>
            <Text bodySize="m" className={styles['config-section']}>
              <b>{t('form-sign-up-swipe-config-add-title-label')}</b>
            </Text>
            <div className={styles['form-elements']}>
              <ControlledTextArea
                control={control}
                labelKey="form-sign-up-swipe-config-title-label"
                name={`translation.${language}.title`}
                showCharacterLimit={false}
                upperLimit={SwipeTranslationSchema.properties.title.maxLength}
              />
            </div>
            <Text bodySize="m" className={styles['config-section']}>
              <b>{t('form-sign-up-swipe-config-add-body-label')}</b>
            </Text>
            <div className={styles['form-elements']}>
              <ControlledTextArea
                control={control}
                labelKey="form-sign-up-swipe-config-body-label"
                name={`translation.${language}.body`}
                showCharacterLimit={false}
                upperLimit={SwipeTranslationSchema.properties.body.maxLength}
              />
            </div>
            <Text bodySize="m" className={styles['config-section']}>
              <b>{t('form-sign-up-swipe-config-attach-start-image-label')}</b>
            </Text>
            <div className={styles['form-elements']}>
              <ControlledTextArea
                control={control}
                labelKey="form-sign-up-swipe-config-start-image-label"
                name={`translation.${language}.links.startPageImage`}
                showCharacterLimit={false}
                upperLimit={
                  SwipeTranslationSchema.properties.links.properties.startPageImage.maxLength
                }
                helpText={'https://www.ikea.com/ext/ingkadam/...'}
              />
            </div>
            <Text bodySize="m" className={styles['config-section']}>
              <b>{t('form-sign-up-swipe-config-attach-winner-image-label')}</b>
            </Text>
            <div className={styles['form-elements']}>
              <ControlledTextArea
                control={control}
                labelKey="form-sign-up-swipe-config-winner-image-label"
                name={`translation.${language}.links.winnerPageImage`}
                showCharacterLimit={false}
                upperLimit={
                  SwipeTranslationSchema.properties.links.properties.winnerPageImage.maxLength
                }
                helpText={'https://www.ikea.com/ext/ingkadam/...'}
              />
            </div>
            <Text bodySize="m" className={styles['config-section']}>
              <b>{t('form-sign-up-swipe-config-attach-loser-image-label')}</b>
            </Text>
            <div className={styles['form-elements']}>
              <ControlledTextArea
                control={control}
                labelKey="form-sign-up-swipe-config-loser-image-label"
                name={`translation.${language}.links.loserPageImage`}
                showCharacterLimit={false}
                upperLimit={
                  SwipeTranslationSchema.properties.links.properties.loserPageImage.maxLength
                }
                helpText={'https://www.ikea.com/ext/ingkadam/...'}
              />
            </div>
          </div>
        </TabPanel>
      ),
    };
  });

  const tabs = tabsAndPanels.map(({ tab }) => tab);
  const panels = tabsAndPanels.map(({ panel }) => panel);

  return (
    <form>
      {!isValid && (
        <div className={styles['container']}>
          <InlineMessage
            className={styles['inline-message']}
            variant="informative"
            title={`${t('form-explanation-message-swipe-translations-title')}`}
            body={`${t('form-explanation-message-swipe-translations-body')}`}
          />
          <InlineMessage
            variant="cautionary"
            title={`${t('form-missing-translations-title')}`}
            body={`${t('form-missing-translations-body')}`}
          />
        </div>
      )}
      <Tabs tabs={tabs} tabPanels={panels} defaultActiveTab={tabsAndPanels[0].id} />
    </form>
  );
};
